import React, { useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import {
  Form,
  Row,
  Col,
  Tabs,
  Tab,
  Button,
  Table,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import isArray from 'lodash/isArray';
import { toast, ToastContainer, Slide } from 'react-toastify';
import { CategoryTreeNoRedux as CategoryTree } from '../CategoryTree';
import { useRootCategory } from './hooks/useRootCategory';
import Pagination from '../PaginationBootstrap';
import Loader from '../ui/Loader';
import { DATE_FORMAT } from '../../constants/session';
import {
  validate,
  generateRequestBody,
  downloadCSV,
  fetchHistory,
} from './utils';
import appConfig from '../../appConfig';

const PAGE_SIZE = 10;
const UMO_ID_KEY = '1';
const CATE_ID_KEY = '2';

const EditHistory = ({ intl }) => {

  const [isRetrievingRoot, categoryList] = useRootCategory();
  const [history, sethistory] = useState([]);
  const [pageStart, setPageStart] = useState(1);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState({
    umoID: '',
    dateFrom: null,
    dateTo: null,
    categoryID: '',
  });
  const dateFormat = useMemo(() => appConfig.dateFormat || DATE_FORMAT, []);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const handleTreeSelect = (selectedKeys, _) => {
    if (isArray(selectedKeys) && selectedKeys.length > 0) {
      setQuery({ ...query, categoryID: selectedKeys[0] });
    } else {
      setQuery({ ...query, categoryID: '' });
    }
  };

  const queryHistory = async (queryParam, pageStartParam) => {
    const errMsg = validate(query);
    if (errMsg) {
      toast.error(errMsg);
      return;
    }
    setIsLoading(true);
    try {
      const { results, total: t } = await fetchHistory(
        queryParam,
        pageStartParam,
        PAGE_SIZE
      );
      sethistory(results);
      setTotal(t);
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    queryHistory(query, pageStart);
    // console.log(ret);
  };

  const onDownload = async () => {
    const errMsg = validate(query);
    if (errMsg) {
      toast.error(errMsg);
      return;
    }
    const body = {
      ORDER_BY: 'DATE',
      OUTPUT_FORMAT: 'CSV',
      n: 'NWS_HISTORY',
    };
    setIsDownloading(true);
    const data = generateRequestBody(query, body);
    try {
      await downloadCSV(data);
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePageChange = (index) => {
    const nextPageStart = index * PAGE_SIZE + 1;
    setPageStart(nextPageStart);
    queryHistory(query, nextPageStart);
  };

  const handleSelect = (key) => {
    if (key === UMO_ID_KEY) {
      setQuery({ ...query, categoryID: '' });
    } else {
      setQuery({ ...query, umoID: '' });
    }
    setPageStart(1);
  };

  return (
    <div className="edit-history__container">
      <h1 className="page-header">
        {intl.formatMessage({ id: 'navReportsPanel.editHistory' })}
      </h1>
      <Form onSubmit={onSubmit}>
        <Row>
          <Col className="col-md-6">
            <Tabs
              defaultActiveKey={UMO_ID_KEY}
              onSelect={handleSelect}
              unmountOnExit
            >
              <Tab
                eventKey={UMO_ID_KEY}
                title={intl.formatMessage({ id: 'navReportsPanel.umoID' })}
                className="mt-15"
              >
                <Form.Group>
                  <Form.Control
                    as="input"
                    placeholder={intl.formatMessage({
                      id: 'navReportsPanel.enterUMOID',
                    })}
                    value={query.umoID}
                    onChange={(e) =>
                      setQuery({ ...query, umoID: e.target.value })
                    }
                  />
                </Form.Group>
              </Tab>
              <Tab
                eventKey={CATE_ID_KEY}
                title={intl.formatMessage({ id: 'navReportsPanel.categoryID' })}
                className="mt-15"
              >
                <div className="category-tree-container">
                  <CategoryTree
                    onSelect={handleTreeSelect}
                    categoryList={categoryList}
                    isRetrievingRoot={isRetrievingRoot}
                    disableCollectionSelect
                  />
                </div>
              </Tab>
            </Tabs>
          </Col>
          <Col className="col-md-6">
            <Form.Group>
              <label htmlFor="dateFrom" className="date-label">
                {intl.formatMessage({ id: 'navReportsPanel.dateFrom' })}:
              </label>
              <DatePicker
                isClearable
                placeholderText={dateFormat.toLocaleLowerCase()}
                disabled={isLoading || isDownloading}
                selected={query.dateFrom}
                onChange={(date) =>
                  setQuery({ ...query, dateFrom: date || null })
                }
                dateFormat={dateFormat}
              />
            </Form.Group>
            <Form.Group>
              <label htmlFor="dateTo" className="date-label">
                {intl.formatMessage({ id: 'navReportsPanel.dateTo' })}:
              </label>
              <DatePicker
                isClearable
                placeholderText={dateFormat.toLocaleLowerCase()}
                disabled={isLoading || isDownloading}
                selected={query.dateTo}
                onChange={(date) =>
                  setQuery({ ...query, dateTo: date || null })
                }
                dateFormat={dateFormat}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="submit-row">
          <Button type="submit" disabled={isLoading} className="col-sm-auto mr-15 submit-button">
            {isLoading ? (
              <Loader />
            ) : (
              intl.formatMessage({ id: 'navReportsPanel.submit' })
            )}
          </Button>
          <Button type="button" disabled={isDownloading} onClick={onDownload} className="col-sm-auto download-button">
            {isDownloading ? (
              <Loader />
            ) : (
              intl.formatMessage({ id: 'navReportsPanel.downloadCSV' })
            )}
          </Button>
        </Row>
      </Form>
      <div>
        <Table responsive>
          <thead>
            <tr>
              <th style={{ width: 100 }}>
                {intl.formatMessage({ id: 'navReportsPanel.umoID' })}
              </th>
              <th>{intl.formatMessage({ id: 'navReportsPanel.date' })}</th>
              <th>{intl.formatMessage({ id: 'navReportsPanel.user' })}</th>
              <th>{intl.formatMessage({ id: 'navReportsPanel.tagName' })}</th>
              <th>{intl.formatMessage({ id: 'navReportsPanel.change' })}</th>
            </tr>
          </thead>
          <tbody>
            {history?.map((umoItem, umoIndex) =>
              umoItem.history?.length > 0 ? (
                umoItem.history?.map((hisItem, hisIndex) => (
                  <tr key={`${umoIndex}-${hisIndex}`}>
                    <td>{umoItem.umo_id}</td>
                    <td>{hisItem.data?.date}</td>
                    <td>{hisItem.profile?.username}</td>
                    <td>{hisItem.data?.name}</td>
                    <td>{hisItem.data?.value}</td>
                  </tr>
                ))
              ) : (
                <tr key={umoItem.umo_id}>
                  <td>{umoItem.umo_id}</td>
                  <td> </td>
                  <td> </td>
                  <td> </td>
                  <td> </td>
                </tr>
              )
            )}
          </tbody>
        </Table>
        {isLoading && <Loader />}
        <Pagination
          key={query.umoID || query.categoryID}
          total={total}
          onPageClick={handlePageChange}
          pageSize={PAGE_SIZE}
        />
      </div>
      <ToastContainer
        style={{ top: 52 }}
        position="top-right"
        autoClose={3000}
        transition={Slide}
        closeOnClick
        pasuseOnHover
        hideProgressBar
        // progress={0}
        theme="light"
      />
    </div>
  );
}

export default injectIntl(EditHistory);
